import React, { useMemo, useRef, useState } from "react";
import SummaryCards from "../../components/Common/SummaryCards";
import {
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import {
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from "../../components/Common/AppOverlay";
import "./InspectionCycleReport.scss";
import "../Standards/Standards.scss";

const initialVisibleColumns = [
  "cycleName",
  "cyclePeriod",
  "status",
  "engagements",
  "completed",
  "inProgress",
  "findings",
  "highRisk",
  "leadReviewer",
  "startDate",
  "dueDate",
  "completion",
];

const columnDefinitions = [
  ["cycleName", "Cycle Name"],
  ["cyclePeriod", "Cycle Period"],
  ["status", "Status"],
  ["engagements", "Engagements"],
  ["completed", "Completed"],
  ["inProgress", "In Progress"],
  ["findings", "Findings"],
  ["highRisk", "High-Risk Items"],
  ["leadReviewer", "Lead Reviewer"],
  ["startDate", "Start Date"],
  ["dueDate", "Due Date"],
  ["completion", "Completion %"],
  ["lastUpdated", "Last Updated"],
  ["createdBy", "Created By"],
  ["notes", "Notes"],
];

const reportRows = [
  {
    cycleName: "2024 – Q1 Cycle",
    cyclePeriod: "Jan - Mar 2024",
    status: "Completed",
    engagements: 12,
    completed: 12,
    inProgress: 0,
    findings: 15,
    highRisk: 3,
    leadReviewer: "John Smith",
    startDate: "02 Jan 2024",
    dueDate: "31 Mar 2024",
    completion: 100,
    lastUpdated: "31 Mar 2024",
    createdBy: "ABC Firm Admin",
    notes: "All reviews completed",
  },
  {
    cycleName: "2024 – Q2 Cycle",
    cyclePeriod: "Apr - Jun 2024",
    status: "Active",
    engagements: 14,
    completed: 8,
    inProgress: 6,
    findings: 9,
    highRisk: 2,
    leadReviewer: "Sarah Johnson",
    startDate: "01 Apr 2024",
    dueDate: "30 Jun 2024",
    completion: 57,
    lastUpdated: "17 Jun 2024",
    createdBy: "ABC Firm Admin",
    notes: "Six reviews in progress",
  },
  {
    cycleName: "2024 – Q3 Cycle",
    cyclePeriod: "Jul - Sep 2024",
    status: "Active",
    engagements: 10,
    completed: 3,
    inProgress: 7,
    findings: 6,
    highRisk: 1,
    leadReviewer: "Michael Brown",
    startDate: "01 Jul 2024",
    dueDate: "30 Sep 2024",
    completion: 30,
    lastUpdated: "12 Jul 2024",
    createdBy: "ABC Firm Admin",
    notes: "Cycle underway",
  },
  {
    cycleName: "2024 – Q4 Cycle",
    cyclePeriod: "Oct - Dec 2024",
    status: "Overdue",
    engagements: 11,
    completed: 4,
    inProgress: 7,
    findings: 8,
    highRisk: 4,
    leadReviewer: "Emily Davis",
    startDate: "01 Oct 2024",
    dueDate: "31 Dec 2024",
    completion: 36,
    lastUpdated: "05 Jan 2025",
    createdBy: "ABC Firm Admin",
    notes: "Action required",
  },
  {
    cycleName: "2023 – Annual Cycle",
    cyclePeriod: "Jan - Dec 2023",
    status: "Completed",
    engagements: 18,
    completed: 18,
    inProgress: 0,
    findings: 22,
    highRisk: 5,
    leadReviewer: "Robert Wilson",
    startDate: "01 Jan 2023",
    dueDate: "31 Dec 2023",
    completion: 100,
    lastUpdated: "31 Dec 2023",
    createdBy: "ABC Firm Admin",
    notes: "Annual review closed",
  },
];

const selectOptions = {
  cycle: [
    "All Cycles",
    "2024 – Q1 Cycle",
    "2024 – Q2 Cycle",
    "2024 – Q3 Cycle",
    "2024 – Q4 Cycle",
  ],
  status: ["All Statuses", "Active", "Completed", "Overdue"],
  reviewer: [
    "All Reviewers",
    "John Smith",
    "Sarah Johnson",
    "Michael Brown",
    "Emily Davis",
  ],
  risk: ["All Risk Levels", "High", "Medium", "Low"],
  client: ["All Clients", "Acme Accounting LLP", "Bright & Co.", "Summit CPAs"],
  industry: [
    "All Industries",
    "Accounting",
    "Financial Services",
    "Technology",
  ],
  firm: ["All Firms", "ABC Firm", "ABC Advisory"],
};

const SelectField = ({ label, field, value, onChange }) => (
  <label className="inspection-report__field">
    <span>{label}</span>
    <select
      value={value}
      onChange={(event) => onChange(field, event.target.value)}
    >
      {selectOptions[field].map((option) => (
        <option key={option}>{option}</option>
      ))}
    </select>
  </label>
);

const DateField = ({ label, value, onChange }) => (
  <label className="inspection-report__field">
    <span>{label}</span>
    <div className="inspection-report__date">
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Select date"
      />
      <i className="bx bx-calendar" />
    </div>
  </label>
);

const statusClass = (status) => status.toLowerCase().replace(" ", "-");

const InspectionCycleReport = () => {
  const resultsRef = useRef(null);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [filters, setFilters] = useState({
    cycle: "All Cycles",
    status: "All Statuses",
    reviewer: "All Reviewers",
    risk: "All Risk Levels",
    client: "All Clients",
    industry: "All Industries",
    firm: "All Firms",
    from: "",
    to: "",
  });
  const [resultSearch, setResultSearch] = useState("");
  const [visibleColumns, setVisibleColumns] = useState(initialVisibleColumns);
  const [draftColumns, setDraftColumns] = useState(initialVisibleColumns);

  const updateFilter = (field, value) =>
    setFilters((current) => ({ ...current, [field]: value }));

  const resetFilters = () =>
    setFilters({
      cycle: "All Cycles",
      status: "All Statuses",
      reviewer: "All Reviewers",
      risk: "All Risk Levels",
      client: "All Clients",
      industry: "All Industries",
      firm: "All Firms",
      from: "",
      to: "",
    });
  const appliedFilters = [
    ["status", "Status"],
    ["from", "Cycle Period"],
    ["risk", "Risk Level"],
  ].flatMap(([field, label]) => {
    const value = filters[field];
    const defaultValue = selectOptions[field]?.[0];
    return value && value !== defaultValue
      ? [[field, `${label}: ${value}`]]
      : [];
  });
  const filteredRows = useMemo(
    () =>
      reportRows.filter((row) => {
        const term = resultSearch.trim().toLowerCase();
        const statusMatches =
          filters.status === "All Statuses" || row.status === filters.status;
        const cycleMatches =
          filters.cycle === "All Cycles" || row.cycleName === filters.cycle;
        return (
          statusMatches &&
          cycleMatches &&
          (!term || Object.values(row).join(" ").toLowerCase().includes(term))
        );
      }),
    [filters.cycle, filters.status, resultSearch]
  );
  const visibleColumnDefinitions = columnDefinitions.filter(([key]) =>
    visibleColumns.includes(key)
  );

  const toggleDraftColumn = (key) =>
    setDraftColumns((current) =>
      current.includes(key)
        ? current.filter((column) => column !== key)
        : [...current, key]
    );

  const removeFilter = (field) =>
    updateFilter(field, selectOptions[field]?.[0] || "");

  const toggleResultsFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await resultsRef.current?.requestFullscreen();
    } catch (error) {
      console.error("Unable to toggle report results fullscreen.", error);
    }
  };

  const exportReport = (format, event) => {
    event?.stopPropagation();
    setExportOpen(false);
    if (format === "pdf") {
      window.print();
      return;
    }
    const headers = columnDefinitions.filter(([key]) =>
      visibleColumns.includes(key)
    );

    const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const csv = [
      headers.map(([, label]) => escape(label)).join(","),
      ...filteredRows.map((row) =>
        headers.map(([key]) => escape(row[key])).join(",")
      ),
    ].join("\n");
    const file = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    link.download = `inspection-cycle-report.${
      format === "excel" ? "csv" : "csv"
    }`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="inspection-report page-content piers-page">
      <Container fluid>
        <section className="inspection-report__workspace">
          <header className="inspection-report__heading piers-page-header">
            <div>
              <h1 className="piers-page-title">
                Inspection Cycle Report{" "}
                <button
                  type="button"
                  aria-label="Add Inspection Cycle Report to favourites"
                >
                  <i className="bx bx-star" />
                </button>
              </h1>
              <p>
                Report on who has accessed the documents across inspection
                cycles.
              </p>
            </div>
            <div className="inspection-report__actions">
              <div className="inspection-report__export-split">
                <button
                  className="inspection-report__export-main"
                  type="button"
                  onClick={(event) => exportReport("csv", event)}
                >
                  <i className="bx bx-download" /> Export
                </button>
                <Dropdown
                  className="inspection-report__export"
                  isOpen={exportOpen}
                  toggle={() => setExportOpen((open) => !open)}
                >
                  <DropdownToggle
                    className="inspection-report__export-toggle"
                    tag="button"
                    type="button"
                    aria-label="Choose export format"
                  >
                    <i className="bx bx-chevron-down" />
                  </DropdownToggle>
                  <DropdownMenu end>
                    <DropdownItem
                      onClick={(event) => exportReport("csv", event)}
                    >
                      <i className="bx bx-file" /> CSV
                    </DropdownItem>
                    <DropdownItem
                      onClick={(event) => exportReport("excel", event)}
                    >
                      <i className="bx bx-spreadsheet" /> Excel
                    </DropdownItem>
                    <DropdownItem
                      onClick={(event) => exportReport("pdf", event)}
                    >
                      <i className="bx bx-file-blank" /> PDF
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </div>
          </header>

          <section className="inspection-report__filters">
            <header>
              <b>
                <i className="bx bx-slider-alt" /> Search Parameters
              </b>
              <button
                type="button"
                onClick={() => setFiltersOpen((open) => !open)}
              >
                {filtersOpen ? "Hide Filters" : "Show Filters"}{" "}
                <i className={`bx bx-chevron-${filtersOpen ? "up" : "down"}`} />
              </button>
            </header>
            {filtersOpen && (
              <div className="inspection-report__filter-content">
                <div className="inspection-report__filter-grid">
                  <SelectField
                    label="Cycle"
                    field="cycle"
                    value={filters.cycle}
                    onChange={updateFilter}
                  />
                  <SelectField
                    label="Status"
                    field="status"
                    value={filters.status}
                    onChange={updateFilter}
                  />
                  <DateField
                    label="Cycle Period From"
                    value={filters.from}
                    onChange={(value) => updateFilter("from", value)}
                  />
                  <DateField
                    label="Cycle Period To"
                    value={filters.to}
                    onChange={(value) => updateFilter("to", value)}
                  />
                  <SelectField
                    label="Lead Reviewer"
                    field="reviewer"
                    value={filters.reviewer}
                    onChange={updateFilter}
                  />
                  <SelectField
                    label="Risk Level"
                    field="risk"
                    value={filters.risk}
                    onChange={updateFilter}
                  />
                  <SelectField
                    label="Client"
                    field="client"
                    value={filters.client}
                    onChange={updateFilter}
                  />
                  <SelectField
                    label="Industry"
                    field="industry"
                    value={filters.industry}
                    onChange={updateFilter}
                  />
                  <SelectField
                    label="Firm"
                    field="firm"
                    value={filters.firm}
                    onChange={updateFilter}
                  />
                </div>
              </div>
            )}
            <div className="inspection-report__applied">
              <b>Applied Filters:</b>
              {appliedFilters.map(([field, label]) => (
                <button
                  type="button"
                  key={field}
                  onClick={() => removeFilter(field)}
                >
                  {label} <i className="bx bx-x" />
                </button>
              ))}
              {!!appliedFilters.length && (
                <button
                  type="button"
                  className="inspection-report__clear"
                  onClick={resetFilters}
                >
                  Clear All
                </button>
              )}
            </div>
          </section>

          <SummaryCards
            items={[
              [
                "bx-clipboard",
                "blue",
                "Total Cycles",
                "24",
                "All inspection cycles",
              ],
              [
                "bx-check-circle",
                "green",
                "Active Cycles",
                "8",
                "33.33% of total",
              ],
              [
                "bx-flag",
                "purple",
                "Completed Cycles",
                "14",
                "58.33% of total",
              ],
              [
                "bx-time-five",
                "orange",
                "Overdue Cycles",
                "2",
                "8.33% of total",
              ],
              [
                "bx-shield-quarter",
                "rose",
                "High Risk Cycles",
                "5",
                "20.83% of total",
              ],
            ]}
            ariaLabel="Inspection report summary"
          />

          <section className="inspection-report__results" ref={resultsRef}>
            <header>
              <h2>
                Report Results <span>({filteredRows.length || 0})</span>
              </h2>
              <div>
                <label>
                  <i className="bx bx-search" />
                  <input
                    value={resultSearch}
                    onChange={(event) => setResultSearch(event.target.value)}
                    placeholder="Search in results..."
                  />
                </label>
                <button
                  type="button"
                  className="is-primary"
                  onClick={() => {
                    setDraftColumns(visibleColumns);
                    setPanelOpen(true);
                  }}
                >
                  <i className="bx bx-columns" /> Columns
                </button>
                <button
                  type="button"
                  aria-label="Fullscreen results"
                  onClick={toggleResultsFullscreen}
                >
                  <i className="bx bx-fullscreen" />
                </button>
              </div>
            </header>
            <div className="inspection-report__table-wrap">
              <table>
                <thead>
                  <tr>
                    {visibleColumnDefinitions.map(([key, label]) => (
                      <th key={key}>
                        {label} <i className="bx bx-sort-alt-2" />
                      </th>
                    ))}
                    <th aria-label="Actions">•••</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.cycleName}>
                      {visibleColumnDefinitions.map(([key]) => (
                        <td key={key}>
                          {key === "status" ? (
                            <span
                              className={`inspection-report__status is-${statusClass(
                                row[key]
                              )}`}
                            >
                              {row[key]}
                            </span>
                          ) : key === "highRisk" ? (
                            <b className="inspection-report__risk">
                              {row[key]}
                            </b>
                          ) : key === "completion" ? (
                            <span className="inspection-report__progress">
                              <i>
                                <em style={{ width: `${row[key]}%` }} />
                              </i>
                              {row[key]}%
                            </span>
                          ) : (
                            row[key]
                          )}
                        </td>
                      ))}
                      <td>
                        <button
                          className="inspection-report__row-menu"
                          type="button"
                          aria-label={`Actions for ${row.cycleName}`}
                        >
                          <i className="bx bx-dots-vertical-rounded" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!filteredRows.length && (
                    <tr>
                      <td
                        className="inspection-report__no-results"
                        colSpan={visibleColumnDefinitions.length + 1}
                      >
                        No inspection cycles match the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <footer>
              <span>Rows per page:</span>
              <button type="button">
                10 <i className="bx bx-chevron-down" />
              </button>
              <b>1–{filteredRows.length} of 24</b>
              <nav>
                <button type="button">
                  <i className="bx bx-first-page" />
                </button>
                <button type="button">
                  <i className="bx bx-chevron-left" />
                </button>
                <button type="button" className="is-active">
                  1
                </button>
                <button type="button">2</button>
                <button type="button">3</button>
                <button type="button">
                  <i className="bx bx-chevron-right" />
                </button>
                <button type="button">
                  <i className="bx bx-last-page" />
                </button>
              </nav>
            </footer>
          </section>
        </section>
      </Container>

      <Offcanvas
        backdrop="static"
        className="standards-drawer inspection-report-columns-drawer"
        direction="end"
        isOpen={panelOpen}
        toggle={() => setPanelOpen(false)}
        aria-label="Columns configuration"
      >
        <OffcanvasHeader
          toggle={() => setPanelOpen(false)}
          className="canvasHeader"
        >
          Columns
        </OffcanvasHeader>
        <OffcanvasBody>
          <section className="inspection-report__columns">
            <b>Show / Hide Columns</b>
            {columnDefinitions.map(([key, label]) => (
              <label key={key}>
                <i className="bx bx-dots-vertical-rounded" />
                <input
                  type="checkbox"
                  checked={draftColumns.includes(key)}
                  onChange={() => toggleDraftColumn(key)}
                />
                <span>{label}</span>
                {initialVisibleColumns.includes(key) && (
                  <i className="bx bx-lock-alt" />
                )}
              </label>
            ))}
          </section>
          <footer className="inspection-report__columns-actions">
            <button
              className="inspection-report__reset-columns"
              type="button"
              onClick={() => setDraftColumns(initialVisibleColumns)}
            >
              Reset to Default
            </button>
            <button
              className="inspection-report__apply-columns"
              type="button"
              onClick={() => {
                setVisibleColumns(draftColumns);
                setPanelOpen(false);
              }}
            >
              Apply
            </button>
          </footer>
        </OffcanvasBody>
      </Offcanvas>
    </main>
  );
};

export default InspectionCycleReport;
